import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { EnhancedLoadingScreen, SimpleLoadingScreen } from './components/common/EnhancedLoadingScreen';
import { OfflineIndicator } from './components/common/OfflineIndicator';
import { SkipLink } from './components/SkipLink';
import { XpGainToast } from './components/XpGainToast';
import { ScrollToTop } from './components/ScrollToTop';
// Performance monitoring loaded asynchronously in development
import { useShallow } from 'zustand/react/shallow';
import { useOptimizedAuth } from './hooks/useOptimizedAuth';
import { useOnboardingCheck } from './hooks/useOnboardingCheck';
import { useUIStore, useSystemThemeListener, useSystemMotionListener } from './stores/uiStore';

// Lazy load components for better performance
const LoginPage = React.lazy(() => import('./pages/auth/login'));
const RegisterPage = React.lazy(() => import('./pages/auth/register'));
const ResetPasswordPage = React.lazy(() => import('./pages/auth/reset-password'));
const UpdatePasswordPage = React.lazy(() => import('./pages/auth/update-password'));
const OnboardingPage = React.lazy(() => import('./pages/onboarding/index'));
const GuidedTour = React.lazy(() => import('./pages/onboarding/GuidedTour'));
const DashboardPage = React.lazy(() => import('./pages/dashboard/index'));
const ChatPage = React.lazy(() => import('./pages/chat/index'));
const GrammarDemoPage = React.lazy(() => import('./pages/chat/GrammarDemo'));
const ReviewPage = React.lazy(() => import('./pages/review/index'));
const SettingsPage = React.lazy(() => import('./pages/settings/index'));
const ScenariosPage = React.lazy(() => import('./pages/scenarios/index'));
const ScenarioPlayerPage = React.lazy(() => import('./pages/scenarios/player/index'));
const LandingPage = React.lazy(() => import('./pages/landing/index'));
const PricingPage = React.lazy(() => import('./pages/landing/PricingPage'));
const CheckoutSuccessPage = React.lazy(() => import('./pages/subscription/CheckoutSuccess'));
const TermsOfServicePage = React.lazy(() => import('./pages/legal/TermsOfService'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/legal/PrivacyPolicy'));

// Loading fallback component for page transitions
function PageLoadingFallback() {
  return <SimpleLoadingScreen message="Loading page..." />;
}

// Route protection component
interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

function ProtectedRoute({
  children,
  redirectTo = '/auth/login'
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, canProceed } = useOptimizedAuth();

  if (isLoading && !canProceed) {
    return <PageLoadingFallback />;
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

// Public route component (redirects authenticated users)
function PublicRoute({
  children,
  redirectTo = '/dashboard'
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, canProceed } = useOptimizedAuth();

  if (isLoading && !canProceed) {
    return <PageLoadingFallback />;
  }

  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

// Onboarding guard -- redirects new users who haven't completed onboarding
function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const { needsOnboarding, isLoading } = useOnboardingCheck();

  if (isLoading) {
    return <PageLoadingFallback />;
  }

  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

// Onboarding completion guard -- redirects users who already completed onboarding
function OnboardingCompletedGuard({ children }: { children: React.ReactNode }) {
  const { needsOnboarding, isLoading } = useOnboardingCheck();

  if (isLoading) {
    return <PageLoadingFallback />;
  }

  // If user doesn't need onboarding (already completed or existing user), redirect to dashboard
  if (!needsOnboarding) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Main App component
function App() {
  const { effectiveTheme, setGlobalError } = useUIStore(
    useShallow(state => ({
      effectiveTheme: state.effectiveTheme,
      setGlobalError: state.setGlobalError
    }))
  );
  const { isLoading, canProceed, loadingStep, progress } = useOptimizedAuth();

  // Listen for system theme and motion changes
  useSystemThemeListener();
  useSystemMotionListener();

  // Apply theme to document
  useEffect(() => {
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(effectiveTheme);
  }, [effectiveTheme]);

  // Global error handler
  const handleGlobalError = (error: Error) => {
    console.error('Global error:', error);
    setGlobalError(error.message, error);
  };

  // Show enhanced loading screen while auth is being determined
  if (isLoading && !canProceed) {
    return (
      <EnhancedLoadingScreen
        currentStep={loadingStep}
        progress={progress}
        showSkeletons={loadingStep === 'ready'}
      />
    );
  }

  return (
    <ErrorBoundary onError={handleGlobalError}>
      <Router>
        <ScrollToTop />
        <div className="min-h-dvh bg-background text-foreground">
          {/* Skip link for keyboard accessibility - must be first focusable element */}
          <SkipLink />

          {/* Global components */}
          <OfflineIndicator />
          <XpGainToast />
          <Analytics />
          {/* Performance monitoring loaded asynchronously in development */}

          {/* Main application routes */}
          <main id="main-content" className="focus:outline-none" tabIndex={-1}>
            <Suspense fallback={<PageLoadingFallback />}>
              <Routes>
              {/* Landing page - public, redirects authenticated users to dashboard */}
              <Route path="/" element={<LandingPage />} />

              {/* Pricing page - public, accessible to both auth and unauth users */}
              <Route path="/pricing" element={<PricingPage />} />

              {/* Checkout success - protected (user must be logged in) */}
              <Route
                path="/subscription/success"
                element={
                  <ProtectedRoute>
                    <CheckoutSuccessPage />
                  </ProtectedRoute>
                }
              />

              {/* Legal pages - public, accessible to all users */}
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              
              {/* Authentication routes */}
              <Route
                path="/auth/login"
                element={
                  <PublicRoute>
                    <LoginPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/auth/register"
                element={
                  <PublicRoute>
                    <RegisterPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/auth/reset-password"
                element={
                  <PublicRoute>
                    <ResetPasswordPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/auth/update-password"
                element={
                  <PublicRoute>
                    <UpdatePasswordPage />
                  </PublicRoute>
                }
              />
              
              {/* Onboarding route -- full-screen tutorial for new users */}
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <OnboardingCompletedGuard>
                      <OnboardingPage />
                    </OnboardingCompletedGuard>
                  </ProtectedRoute>
                }
              />

              {/* Guided first conversation -- 60-second tour, launched after onboarding */}
              <Route
                path="/guided-intro"
                element={
                  <ProtectedRoute>
                    <GuidedTour />
                  </ProtectedRoute>
                }
              />

              {/* Protected application routes (with onboarding guard) */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <DashboardPage />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              {/* /progress redirects to consolidated dashboard */}
              <Route
                path="/progress"
                element={<Navigate to="/dashboard" replace />}
              />
              <Route
                path="/chat"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <ChatPage />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/grammar-demo"
                element={
                  <ProtectedRoute>
                    <GrammarDemoPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/review"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <ReviewPage />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <SettingsPage />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/scenarios"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <ScenariosPage />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/scenarios/:id"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <ScenarioPlayerPage />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />

              {/* Catch-all route */}
              <Route
                path="*"
                element={
                  <div className="flex items-center justify-center min-h-dvh">
                    <div className="text-center">
                      <h1 className="text-4xl font-bold mb-4">404</h1>
                      <p className="text-secondary mb-6">Page not found</p>
                      <a 
                        href="/dashboard" 
                        className="text-primary hover:text-primary-dark"
                      >
                        Go to Dashboard
                      </a>
                    </div>
                  </div>
                } 
              />
              </Routes>
            </Suspense>
          </main>
        </div>
      </Router>
    </ErrorBoundary>
  );
};

export default App;